// Function Matrix Page
// AIAG-VDA Step 3: Function Analysis - GOLDEN THREAD INTEGRATION
// Connects Process Steps (from Step 2) to Product Requirements
// ============================================================

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAnalysis } from '../contexts/AnalysisContext';
import {
    Grid3x3,
    Check,
    AlertCircle,
    ArrowLeft,
    ArrowRight,
    Download,
    Save,
    RefreshCw,
    Plus,
    Link2,
    Users,
} from 'lucide-react';
import clsx from 'clsx';
import { ProductRequirement, ProcessFunction } from '../types';
import ProjectAccessManager from '../components/ProjectAccessManager';
import { NextStepButton } from '../components/WorkflowNavigation';

// ============================================================
// SUB-COMPONENTS (Low Cyclomatic Complexity)
// ============================================================

interface RequirementTypeBadgeProps {
    type: ProductRequirement['type'];
}

const RequirementTypeBadge: React.FC<RequirementTypeBadgeProps> = ({ type }) => {
    const colorMap: Record<ProductRequirement['type'], string> = {
        customer: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        regulatory: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        engineering: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
        safety: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    };
    return (
        <span className={clsx('text-xs px-2 py-0.5 rounded-full', colorMap[type])}>
            {type.toUpperCase()}
        </span>
    );
};

interface FunctionCellButtonProps {
    checked: boolean;
    canEdit: boolean;
    isDark: boolean;
    onClick: () => void;
}

const FunctionCellButton: React.FC<FunctionCellButtonProps> = ({
    checked,
    canEdit,
    isDark,
    onClick,
}) => (
    <button
        onClick={onClick}
        disabled={!canEdit}
        className={clsx(
            'w-8 h-8 rounded border-2 flex items-center justify-center transition-all',
            !canEdit && 'cursor-not-allowed opacity-50',
            checked
                ? 'bg-green-500 border-green-600 text-white'
                : isDark
                    ? 'border-slate-600 hover:border-green-400'
                    : 'border-slate-300 hover:border-green-500'
        )}
        title={checked ? 'Click to unlink' : 'Click to link function'}
    >
        {checked && <Check size={16} />}
    </button>
);

// ============================================================
// EXPORT HELPER
// ============================================================

function exportMatrixToCSV(
    processSteps: { id: string; stepNumber?: string; stepName: string }[],
    productRequirements: ProductRequirement[],
    hasFunction: (stepId: string, reqId: string) => boolean
): void {
    const headers = ['Process Step', ...productRequirements.map(r => r.requirementName)];
    const rows = processSteps.map(step => {
        const row = [`${step.stepNumber} - ${step.stepName}`];
        productRequirements.forEach(req => {
            row.push(hasFunction(step.id, req.id) ? 'X' : '');
        });
        return row;
    });

    const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `function_matrix_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
}

// ============================================================
// MAIN COMPONENT
// ============================================================

const FunctionMatrixPage: React.FC = () => {
    const { activeProjectName } = useAppContext();
    const { hasPermission } = useAuth();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    // Golden Thread: Analysis Context
    const {
        processSteps,
        productRequirements,
        processFunctions,
        linkFunction,
        unlinkFunction,
        addRequirement,
        updateRequirement,
        updateFunction,
        saveAnalysis,
        workElements,
        isSyncing,
    } = useAnalysis();

    // Editing State
    const [editingReqId, setEditingReqId] = useState<string | null>(null);
    const [editReqName, setEditReqName] = useState('');

    // Function Description Editing (Simple Prompt for now to avoid UI clutter)
    const handleEditFunction = (func: ProcessFunction) => {
        const newDesc = prompt('Edit Function Description:', func.functionDescription);
        if (newDesc && newDesc !== func.functionDescription) {
            updateFunction(func.id, { functionDescription: newDesc });
        }
    };


    const canEdit = hasPermission('function_matrix.edit') || hasPermission('pfmea.edit');

    // UI State
    const [showAccessManager, setShowAccessManager] = useState(false);
    const [newReqName, setNewReqName] = useState('');
    const [showAddReq, setShowAddReq] = useState(false);

    // Function Matrix Logic
    const hasFunction = (stepId: string, reqId: string): boolean => {
        return processFunctions.some(
            f => f.processStepId === stepId && f.productRequirementId === reqId
        );
    };

    const getFunction = (stepId: string, reqId: string): ProcessFunction | undefined => {
        return processFunctions.find(
            f => f.processStepId === stepId && f.productRequirementId === reqId
        );
    };

    const toggleFunction = (stepId: string, reqId: string, stepName: string, reqName: string) => {
        const existing = getFunction(stepId, reqId);
        if (existing) {
            unlinkFunction(existing.id);
        } else {
            const description = `Ensure ${reqName} via ${stepName}`;
            linkFunction(stepId, reqId, description);
        }
    };

    // Add new requirement
    const handleAddRequirement = () => {
        if (!newReqName.trim() || !activeProjectName) return;

        const newReq: ProductRequirement = {
            id: `req-${Date.now()}`,
            projectName: activeProjectName,
            requirementNumber: `REQ-${String(productRequirements.length + 1).padStart(3, '0')}`,
            requirementName: newReqName.trim(),
            description: '',
            type: 'engineering',
            linkedProductCharacteristics: [],
        };

        addRequirement(newReq);
        setNewReqName('');
        setShowAddReq(false);
    };

    // Stats
    const stats = useMemo(() => ({
        totalReqs: productRequirements.length,
        totalSteps: processSteps.length,
        totalLinks: processFunctions.length,
        coverage: processSteps.length > 0 && productRequirements.length > 0
            ? Math.round((processFunctions.length / (processSteps.length * productRequirements.length)) * 100)
            : 0,
    }), [productRequirements, processSteps, processFunctions]);

    // Empty State
    if (processSteps.length === 0) {
        return (
            <div className={clsx('h-full flex flex-col', isDark ? 'bg-slate-900' : 'bg-slate-50')}>
                <header className={clsx(
                    'h-14 border-b flex items-center justify-between px-4 shrink-0',
                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                )}>
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/process-flow')} className="p-1.5 hover:bg-slate-700 rounded">
                            <ArrowLeft size={18} />
                        </button>
                        <Grid3x3 size={20} className="text-purple-500" />
                        <h1 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                            Function Matrix
                        </h1>
                    </div>
                </header>

                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md">
                        <Link2 size={64} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                        <h2 className={clsx('text-xl font-bold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                            No Process Steps Available
                        </h2>
                        <p className="text-slate-500 mb-6">
                            To use the Function Matrix, you first need to define process steps in the Process Flow editor and click "Extract" to add them to the analysis.
                        </p>
                        <button
                            onClick={() => navigate('/process-flow')}
                            className="flex items-center gap-2 mx-auto px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg"
                        >
                            <ArrowLeft size={16} />
                            Go to Process Flow
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={clsx('h-full flex flex-col', isDark ? 'bg-slate-900' : 'bg-slate-50')}>
            {/* Header */}
            <header className={clsx(
                'h-14 border-b flex items-center justify-between px-4 shrink-0',
                isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
            )}>
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/process-flow')} className="p-1.5 hover:bg-slate-700 rounded">
                        <ArrowLeft size={18} />
                    </button>
                    <Grid3x3 size={20} className="text-purple-500" />
                    <div>
                        <h1 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                            Function Matrix
                        </h1>
                        <p className="text-xs text-slate-500">AIAG-VDA Step 3: Function Analysis</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Add Requirement Button */}
                    <button
                        onClick={() => setShowAddReq(!showAddReq)}
                        className={clsx(
                            'flex items-center gap-2 px-3 py-1.5 rounded text-sm',
                            isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-100 hover:bg-slate-200'
                        )}
                    >
                        <Plus size={16} />
                        Add Requirement
                    </button>

                    {/* Export */}
                    <button
                        onClick={() => exportMatrixToCSV(processSteps, productRequirements, hasFunction)}
                        className={clsx(
                            'flex items-center gap-2 px-3 py-1.5 rounded text-sm',
                            isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-100 hover:bg-slate-200'
                        )}
                    >
                        <Download size={16} />
                        Export
                    </button>

                    {/* Project Access */}
                    <button
                        onClick={() => setShowAccessManager(true)}
                        className={clsx(
                            'flex items-center gap-2 px-3 py-1.5 rounded text-sm',
                            isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-100 hover:bg-slate-200'
                        )}
                    >
                        <Users size={16} />
                        Access
                    </button>

                    {/* Continue to Failure Analysis */}
                    <button
                        onClick={() => navigate('/failure-net')}
                        className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded text-sm"
                    >
                        Continue
                        <ArrowRight size={16} />
                    </button>

                    {/* Save */}
                    <button
                        onClick={saveAnalysis}
                        disabled={isSyncing}
                        className={clsx(
                            'flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium',
                            'bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50'
                        )}
                    >
                        {isSyncing ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                        Save
                    </button>
                </div>
            </header>

            {/* Add Requirement Form */}
            {showAddReq && (
                <div className={clsx(
                    'mx-4 mt-4 p-3 rounded-lg border flex items-center gap-3',
                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                )}>
                    <input
                        type="text"
                        value={newReqName}
                        onChange={e => setNewReqName(e.target.value)}
                        placeholder="Requirement name (e.g., Weld Strength)"
                        className={clsx(
                            'flex-1 px-3 py-2 rounded border text-sm',
                            isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                        )}
                        onKeyDown={e => e.key === 'Enter' && handleAddRequirement()}
                    />
                    <button
                        onClick={handleAddRequirement}
                        className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded text-sm"
                    >
                        Add
                    </button>
                    <button
                        onClick={() => setShowAddReq(false)}
                        className="px-4 py-2 text-slate-500 hover:text-slate-700 text-sm"
                    >
                        Cancel
                    </button>
                </div>
            )}

            {/* Info Banner */}
            <div className={clsx(
                'mx-4 mt-4 p-3 rounded-lg border flex items-start gap-3',
                isDark ? 'bg-blue-900/20 border-blue-700/50' : 'bg-blue-50 border-blue-200'
            )}>
                <AlertCircle size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                    <p className={clsx('font-medium mb-1', isDark ? 'text-blue-300' : 'text-blue-900')}>
                        Golden Thread: Step 2 → Step 3
                    </p>
                    <p className={isDark ? 'text-blue-200/80' : 'text-blue-800/80'}>
                        Process steps extracted from your flow diagram are shown below. Click checkboxes to link each step to the requirements it ensures. When done, click "Continue" to define failure chains.
                    </p>
                </div>
            </div>

            {/* Matrix */}
            <div className="flex-1 overflow-auto p-4">
                <div className={clsx(
                    'rounded-lg border overflow-hidden',
                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                )}>
                    <table className="w-full">
                        <thead>
                            <tr className={isDark ? 'bg-slate-700/50' : 'bg-slate-50'}>
                                <th className={clsx(
                                    'px-4 py-3 text-left text-sm font-semibold sticky left-0 z-10',
                                    isDark ? 'bg-slate-700/50 text-white' : 'bg-slate-50 text-slate-800'
                                )}>
                                    Process Step ({processSteps.length})
                                </th>
                                {productRequirements.length === 0 ? (
                                    <th className="px-4 py-3 text-center min-w-[200px] text-slate-400 italic">
                                        No requirements defined. Click "Add Requirement" to start.
                                    </th>
                                ) : (
                                    productRequirements.map(req => (
                                        <th key={req.id} className="px-4 py-3 text-center min-w-[200px] align-top">
                                            <div className="flex flex-col items-center gap-1">
                                                <RequirementTypeBadge type={req.type} />
                                                <span className="text-xs font-mono text-purple-500">
                                                    {req.requirementNumber}
                                                </span>

                                                {/* Editable Name */}
                                                {editingReqId === req.id ? (
                                                    <input
                                                        type="text"
                                                        value={editReqName}
                                                        onChange={(e) => setEditReqName(e.target.value)}
                                                        onBlur={() => {
                                                            if (editReqName !== req.requirementName) {
                                                                updateRequirement(req.id, { requirementName: editReqName });
                                                            }
                                                            setEditingReqId(null);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                updateRequirement(req.id, { requirementName: editReqName });
                                                                setEditingReqId(null);
                                                            }
                                                        }}
                                                        autoFocus
                                                        className="text-sm font-semibold text-center w-full bg-white dark:bg-slate-700 border rounded px-1"
                                                    />
                                                ) : (
                                                    <span
                                                        className={clsx('text-sm font-semibold cursor-pointer hover:underline decoration-dashed', isDark ? 'text-white' : 'text-slate-800')}
                                                        onClick={() => {
                                                            if (canEdit) {
                                                                setEditingReqId(req.id);
                                                                setEditReqName(req.requirementName);
                                                            }
                                                        }}
                                                        title="Click to edit requirement name"
                                                    >
                                                        {req.requirementName}
                                                    </span>
                                                )}

                                                {req.specification && (
                                                    <span className="text-xs text-slate-400 font-normal">
                                                        {req.specification}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ))
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {processSteps.map((step, idx) => (
                                <tr
                                    key={step.id}
                                    className={clsx(
                                        'border-t',
                                        isDark ? 'border-slate-700 hover:bg-slate-700/30' : 'border-slate-200 hover:bg-slate-50',
                                        idx % 2 === 0 && (isDark ? 'bg-slate-800/50' : 'bg-white')
                                    )}
                                >
                                    <td className={clsx(
                                        'px-4 py-3 sticky left-0 z-10 align-top',
                                        isDark ? 'bg-slate-800' : 'bg-white'
                                    )}>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-mono text-purple-500 font-bold bg-purple-100 dark:bg-purple-900/30 px-1.5 rounded">
                                                    {step.stepNumber}
                                                </span>
                                                <span className={clsx('text-sm font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                                                    {step.stepName}
                                                </span>
                                            </div>

                                            {/* Level 3: Work Elements Visualization */}
                                            {workElements.filter(we => we.processStepId === step.id).length > 0 && (
                                                <div className="ml-2 pl-2 border-l-2 border-slate-300 dark:border-slate-600">
                                                    <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Work Elements (4M)</div>
                                                    {workElements
                                                        .filter(we => we.processStepId === step.id)
                                                        .map(we => (
                                                            <div key={we.id} className="text-xs text-slate-500 flex items-center gap-1 mb-0.5">
                                                                <div className="w-1 h-1 rounded-full bg-slate-400"></div>
                                                                {we.name}
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {productRequirements.length === 0 ? (
                                        <td className="px-4 py-3 text-center text-slate-400">—</td>
                                    ) : (
                                        productRequirements.map(req => {
                                            const checked = hasFunction(step.id, req.id);
                                            const func = getFunction(step.id, req.id);
                                            return (
                                                <td key={req.id} className="px-4 py-3 text-center align-top border-l border-slate-100 dark:border-slate-700">
                                                    <div className="flex flex-col items-center gap-2">
                                                        <FunctionCellButton
                                                            checked={checked}
                                                            canEdit={canEdit}
                                                            isDark={isDark}
                                                            onClick={() => toggleFunction(step.id, req.id, step.stepName, req.requirementName)}
                                                        />
                                                        {func && (
                                                            <div
                                                                className={clsx(
                                                                    "text-xs p-1 rounded max-w-[160px] text-left cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors",
                                                                    isDark ? "text-slate-300" : "text-slate-600"
                                                                )}
                                                                onClick={() => canEdit && handleEditFunction(func)}
                                                                title="Click to edit function description"
                                                            >
                                                                {func.functionDescription}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Summary */}
                <div className={clsx(
                    'mt-4 p-4 rounded-lg border',
                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                )}>
                    <h3 className={clsx('font-semibold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                        Coverage Summary
                    </h3>
                    <div className="grid grid-cols-4 gap-4">
                        <div>
                            <div className="text-sm text-slate-500">Requirements</div>
                            <div className={clsx('text-2xl font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                                {stats.totalReqs}
                            </div>
                        </div>
                        <div>
                            <div className="text-sm text-slate-500">Process Steps</div>
                            <div className={clsx('text-2xl font-bold', isDark ? 'text-white' : 'text-slate-800')}>
                                {stats.totalSteps}
                            </div>
                        </div>
                        <div>
                            <div className="text-sm text-slate-500">Function Links</div>
                            <div className={clsx('text-2xl font-bold text-green-500')}>
                                {stats.totalLinks}
                            </div>
                        </div>
                        <div>
                            <div className="text-sm text-slate-500">Matrix Coverage</div>
                            <div className={clsx(
                                'text-2xl font-bold',
                                stats.coverage >= 50 ? 'text-green-500' : 'text-amber-500'
                            )}>
                                {stats.coverage}%
                            </div>
                        </div>
                    </div>

                    {/* Continue to Failure Analysis */}
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                        <NextStepButton currentStep={3} isComplete={stats.totalLinks > 0} />
                    </div>
                </div>
            </div>

            {/* Project Access Modal */}
            <ProjectAccessManager
                isOpen={showAccessManager}
                onClose={() => setShowAccessManager(false)}
                projectName={activeProjectName || 'Unknown Project'}
            />
        </div>
    );
};

export default FunctionMatrixPage;
